import React, { useState, useEffect } from 'react';
import { IoDocumentTextOutline, IoTimeOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline, IoHourglassOutline, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function OpenPositionsContent() {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // API'den veri çekme
    useEffect(() => {
        fetchOpenPositions();
    }, []);

    const fetchOpenPositions = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await api.getOpenPositions();

            if (result.success) {
                setPositions(result.data || []);
            } else {
                throw new Error(result.message || t('openPositionsContent.dataFetchError'));
            }
        } catch (err) {
            console.error('API Error:', err);
            setError(err.message || t('openPositionsContent.errorLoadingData'));
            toast.error(t('openPositionsContent.errorLoadingData'));
        } finally {
            setLoading(false);
        }
    };

    // Tarih formatını düzenleme
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // Action type'ı metne çevirme
    const getActionTypeText = (actionType) => {
        switch (actionType) {
            case 0:
                return t('openPositionsContent.actionTypes.buy');
            case 1:
                return t('openPositionsContent.actionTypes.sell');
            default:
                return t('openPositionsContent.actionTypes.unknown');
        }
    };

    // Kar/zarar rengini belirleme
    const getProfitColor = (profit) => {
        if (profit > 0) return 'text-success';
        if (profit < 0) return 'text-danger';
        return 'text-muted';
    };

    const totalPages = Math.ceil(positions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = positions.slice(startIndex, endIndex);

    return (
        <div className="crypto-withdrawals-content">
            <div className="change-password-header">
                <h1>{t('openPositionsContent.title')}</h1>
                <p>{t('openPositionsContent.subtitle')}</p>
            </div>

            <div className="content-body">
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>{t('openPositionsContent.loading')}</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p>{t('openPositionsContent.error')} {error}</p>
                        <button onClick={fetchOpenPositions} className="retry-btn">
                            {t('openPositionsContent.retryButton')}
                        </button>
                    </div>
                ) : (
                    <div className="open-positions-table-container">
                        {currentData.length === 0 ? (
                            <div className="no-data">
                                <div className="no-data-content">
                                    <IoDocumentTextOutline size={48} />
                                    <p>{t('openPositionsContent.noDataMessage')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="open-positions-table">
                                {/* Header */}
                                <div className="open-positions-table-header">
                                    <div className="open-positions-table-cell">{t('openPositionsContent.tableHeaders.instrument')}</div>
                                    <div className="open-positions-table-cell">{t('openPositionsContent.tableHeaders.actionType')}</div>
                                    <div className="open-positions-table-cell">{t('openPositionsContent.tableHeaders.amount')}</div>
                                    <div className="open-positions-table-cell">{t('openPositionsContent.tableHeaders.openPrice')}</div>
                                    <div className="open-positions-table-cell">{t('openPositionsContent.tableHeaders.currentPrice')}</div>
                                    <div className="open-positions-table-cell">{t('openPositionsContent.tableHeaders.profitLoss')}</div>
                                    <div className="open-positions-table-cell">{t('openPositionsContent.tableHeaders.margin')}</div>
                                    <div className="open-positions-table-cell">{t('openPositionsContent.tableHeaders.openDate')}</div>
                                </div>

                                {/* Body */}
                                <div className="open-positions-table-body">
                                    {currentData.map((item) => (
                                        <div key={item.orderID} className="open-positions-table-row">
                                            <div className="open-positions-table-cell" data-label={t('openPositionsContent.tableHeaders.instrument')}>
                                                <div className="coin-info">
                                                    <span className="coin-symbol">{item.instrumentName}</span>
                                                </div>
                                            </div>
                                            <div className="open-positions-table-cell" data-label={t('openPositionsContent.tableHeaders.actionType')}>
                                                <div className="amount-info">
                                                    <span className="amount">{getActionTypeText(item.actionType)}</span>
                                                </div>
                                            </div>
                                            <div className="open-positions-table-cell" data-label={t('openPositionsContent.tableHeaders.amount')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.amount}</span>
                                                </div>
                                            </div>
                                            <div className="open-positions-table-cell" data-label={t('openPositionsContent.tableHeaders.openPrice')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.openRate}</span>
                                                </div>
                                            </div>
                                            <div className="open-positions-table-cell" data-label={t('openPositionsContent.tableHeaders.currentPrice')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.currentRate}</span>
                                                </div>
                                            </div>
                                            <div className="open-positions-table-cell" data-label={t('openPositionsContent.tableHeaders.profitLoss')}>
                                                <div className="amount-info">
                                                    <span className={`amount ${getProfitColor(item.profitInAccountCurrency)}`}>
                                                        {item.profitInAccountCurrency}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="open-positions-table-cell" data-label={t('openPositionsContent.tableHeaders.margin')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.marginInDepositCurrency}</span>
                                                </div>
                                            </div>
                                            <div className="open-positions-table-cell" data-label={t('openPositionsContent.tableHeaders.openDate')}>
                                                <div className="date-info">
                                                    <span className="date">{formatDate(item.openTime)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <div className="pagination">
                                    <button
                                        className="pagination-btn prev-btn"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <IoChevronBackOutline size={16} />
                                    </button>

                                    {/* Show current page and next 2 pages */}
                                    {Array.from({ length: Math.min(3, totalPages - currentPage + 1) }, (_, i) => {
                                        const page = currentPage + i;
                                        if (page > totalPages) return null;
                                        return (
                                            <button
                                                key={page}
                                                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button
                                        className="pagination-btn next-btn"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <IoChevronForwardOutline size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
